import { computed } from 'vue'
import {
  type HabilitationDTO,
  type HabilitationDemandeDTO,
  type PermissionDTO,
} from '@/features/api/habilitation/dto/habilitation'
import {
  type NiveauPermission,
  type TypePermission,
  TypePermissionLibelle,
  TypePublic,
  TypePublicLibelle,
} from '@/features/domaine/types/opco'
import {
  isNotNullOrUndefined,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
} from '@/features/shared/tools/object'
import { dateFormatter } from '@/features/shared/tools/date'
import {
  useHabilitationsAdminContentStore
} from '@/features/interface/profil-utilisateur/sections/admin/useHabilitationsAdminContentStore.ts'
import { storeToRefs } from 'pinia'
import type {
  HabilitationsChangedReason
} from '@/features/interface/profil-utilisateur/sections/admin/HabilitationsAdmin.type.ts'
import { useDialog } from '@/features/interface/dialog/useDialog'
import type { DropdownItem } from '@designSystem/components'

export type HabilitationEnAttente = Pick<HabilitationDTO, 'typeEntreprise' | 'typeEntiteJuridique' | 'entite'> & {
  type: 'courrier' | 'web'
  ligne1?: string
  ligne2?: string
}

export type PermissionViewBaseItem = PermissionDTO & {
  libelle: (typeof TypePermissionLibelle)[TypePermission]
  isReadOnly: boolean
}
export type PermissionViewOuiNonItem = PermissionViewBaseItem & {
  mode: 'OuiNon'
  switchPermission?: () => Promise<void>
}
export type PermissionViewEcrireLireAucunItem = PermissionViewBaseItem & {
  mode: 'EcrireLireAucun'
  setPermission?: (niveau: NiveauPermission) => Promise<void>
}

export type PermissionViewItem = PermissionViewOuiNonItem | PermissionViewEcrireLireAucunItem

export type TypePublicDropdownItem = TypePublic | '*'
export type HabilitationViewItem = {
  estHabilitation: true
  type: 'habilitation'
  estAdministrateur: boolean
  estProprietaireHabilitation: boolean
  userNomPrenom: string
  userEmail: string
  userId: string
  entiteDenomination: string
  entiteSirenOuSiret: string
  navigateToUserAllowed: boolean
  navigateToUser?: () => Promise<void>
  navigateToEntiteAllowed: boolean
  navigateToEntite?: () => Promise<void>
  permissionsEcriture: TypePermission[]
  permissionsLecture: TypePermission[]
  typesPublicsLibelle: string
  typesPublicsAvecPlusieursPotentiel: boolean
  typesPublicsDropdownValueUpdate: (publics: TypePublicDropdownItem) => Promise<void>
  typesPublicsDropdownItems: DropdownItem<TypePublicDropdownItem>[]
  typesPublicsDropdownValue?: TypePublicDropdownItem
  deleteHabilitation?: () => Promise<void>
  permissions: PermissionViewItem[]
  permissionAdministrateur: PermissionViewOuiNonItem
}

export type VisiteurViewItem = {
  estHabilitation: true
  type: 'visiteur'
  userNomPrenom: string
  userEmail: string
  userId: string
  entiteDenomination: string
  entiteSirenOuSiret: string
  navigateToUserAllowed: boolean
  navigateToUser?: () => Promise<void>
  navigateToEntiteAllowed: boolean
  navigateToEntite?: () => Promise<void>
  setActivation: (estAccepté: boolean) => Promise<void>
}
export type AttenteAdminViewItem = {
  type: 'attente-admin'
  estHabilitation: false
  entiteDenomination: string
  entiteSirenOuSiret: string
  navigateToUserAllowed: false
  navigateToEntiteAllowed: false
  ligne1: string
}
export type AttenteCourrierViewItem = {
  type: 'attente-courrier'
  estHabilitation: false
  entiteDenomination: string
  entiteSirenOuSiret: string
  navigateToUserAllowed: false
  navigateToEntiteAllowed: false
  ligne1: string
  ligne2: string
}

export type HabilitationsAdminContentHabilitationsProviderOptions = {
  emitAutorisationsChanged: (reason: HabilitationsChangedReason, habilitation: HabilitationDTO) => void
}
export function useHabilitationsAdminContentHabilitationsProvider(options: HabilitationsAdminContentHabilitationsProviderOptions) {
  const store = useHabilitationsAdminContentStore()
  const { allowNavigate, dataToken, engineState, isReadOnly, habilitationsEffectives, habilitationsDemande } =
    storeToRefs(store)
  const dialog = useDialog()

  function convertHabilitationToViewItem(item: HabilitationDTO): HabilitationViewItem | VisiteurViewItem {
    const navigateToUserAllowed = allowNavigate.value && isNotNullUndefinedOrEmptyString(item.contact?.userId)
    const navigateToUser = navigateToUserAllowed ? () => store.navigateTo('contact', item.contact?.email) : undefined

    const navigateToEntiteAllowed = allowNavigate && isNotNullUndefinedOrEmptyString(item.entite.siren)
    const idEntite = item.entite.nic ? `${item.entite.siren}${item.entite.nic}` : item.entite.siren
    const navigateToEntite = navigateToEntiteAllowed ? () => store.navigateTo('entite', idEntite) : undefined

    const estProprietaireHabilitation =
      engineState.value.type === 'administrateur' && dataToken.value?.userId === item.userId

    const result: Partial<HabilitationViewItem | VisiteurViewItem> = {
      estHabilitation: true,
      userNomPrenom: `${item.contact?.prenom ?? ''} ${item.contact?.nom ?? ''}`.trim(),
      userEmail: item.contact?.email ?? '',
      userId: item.userId,
      entiteDenomination: item.entite.denomination,
      entiteSirenOuSiret: item.entite.nic ? `${item.entite.siren} ${item.entite.nic}` : item.entite.siren,
      navigateToUserAllowed,
      navigateToUser,
      navigateToEntiteAllowed,
      navigateToEntite,
      deleteHabilitation:
        engineState.value.type === 'administrateur' && dataToken.value?.userId !== item.userId
          ? buildShowDeleteHabilitation(item)
          : undefined,
    }

    if (item.autorisations.statut === 'Visiteur') {
      return {
        type: 'visiteur',
        setActivation: buildShowSetActivation(item),
        ...result,
      } as VisiteurViewItem
    } else {
      const extraitTypesPermissions = (niveau: NiveauPermission) =>
        item.autorisations.permissions
          .filter((p) => p.niveau === niveau)
          .filter((p) => !['Administration'].includes(p.type))
          .map((p) => p.type)

      const fabriqueLibelleTypePublic = () => {
        if (item.autorisations.publics.length === 0) return '<Absent>'
        if (
          item.autorisations.publics.length > 1 &&
          item.autorisations.publics.length === item.entite.publicsPotentiel.length
        )
          return 'Tous'
        return item.autorisations.publics.map((t) => TypePublicLibelle[t.type]).join(', ')
      }

      const permissions = item.autorisations.permissions
        .filter((p) => p.niveau !== 'NC')
        .map<PermissionViewItem>((p) => ({
          ...p,
          mode: ['RIB', 'Facture', 'DR', 'Virement', 'Administration'].includes(p.type) ? 'OuiNon' : 'EcrireLireAucun',
          libelle: TypePermissionLibelle[p.type],
          isReadOnly: isReadOnly.value,
        }))
        .map<PermissionViewItem>((p) => {
          if (isReadOnly.value) return p
          if (p.mode !== 'OuiNon') return p
          if (estProprietaireHabilitation && p.type === 'Administration') return p

          const switchPermission = async () => {
            const niveau = p.niveau === 'Ecriture' ? 'Aucun' : 'Ecriture'
            await store.setNiveauPermission({ habilitation: item, type: p.type, niveau })
            const habilitation = habilitationsEffectives.value?.find((h) => h.id === item.id)
            options.emitAutorisationsChanged('Update', habilitation!)
          }
          return {
            ...p,
            switchPermission,
          } as PermissionViewOuiNonItem
        })
        .map((p) => {
          if (isReadOnly.value) return p
          if (p.mode !== 'EcrireLireAucun') return p

          const setPermission = async (niveau: NiveauPermission) => {
            await store.setNiveauPermission({ habilitation: item, type: p.type, niveau })
            const habilitation = habilitationsEffectives.value?.find((h) => h.id === item.id)
            options.emitAutorisationsChanged('Update', habilitation!)
          }
          return {
            ...p,
            setPermission,
          } as PermissionViewEcrireLireAucunItem
        })
        .sort((a, b) => a.mode.localeCompare(b.mode))


      const viewItem: HabilitationViewItem = {
        ...(result as HabilitationViewItem),
        type: 'habilitation',
        permissionsEcriture: extraitTypesPermissions('Ecriture'),
        permissionsLecture: extraitTypesPermissions('Lecture'),
        typesPublicsLibelle: fabriqueLibelleTypePublic(),
        typesPublicsAvecPlusieursPotentiel: item.entite.publicsPotentiel.length > 1,
        typesPublicsDropdownValueUpdate: buildTypePublicDropdownValueUpdate(item),
        typesPublicsDropdownItems: getTypesPublicsDropdownItems(item),
        typesPublicsDropdownValue: getTypesPublicsDropdownValue(item),
        estAdministrateur: item.autorisations.statut === 'Administrateur',
        estProprietaireHabilitation,
        permissions: permissions.filter((p) => !['Administration'].includes(p.type)),
        permissionAdministrateur: permissions.find((p) => p.type === 'Administration') as PermissionViewOuiNonItem,
      }

      return viewItem
    }
  }

  function convertAttenteHabilitationToViewItem(
    item: HabilitationDemandeDTO,
  ): AttenteAdminViewItem | AttenteCourrierViewItem | undefined {
    const result: Partial<AttenteAdminViewItem | AttenteCourrierViewItem> = {
      estHabilitation: false,
      entiteDenomination: item.entite.denomination,
      entiteSirenOuSiret: item.entite.nic ? `${item.entite.siren} ${item.entite.nic}` : item.entite.siren,
      navigateToUserAllowed: false,
      navigateToEntiteAllowed: false,
    }

    if (item.registrations?.length > 0) {
      const registration = [...item.registrations]
        .sort((a, b) => {
          if (a.dateCreation < b.dateCreation) return -1
          if (a.dateCreation > b.dateCreation) return 1
          return 0
        })
        .at(0)
      const ligne1 = `Un courrier a été envoyé le ${dateFormatter(registration?.dateEnvoi)} à`
      const ligne2 = [registration?.entite?.denomination, ...Object.values(registration?.adresseCourrier ?? {})]
        .filter((value) => isNotNullUndefinedOrEmptyString(value))
        .join(', ')
      return {
        type: 'attente-courrier',
        ligne1,
        ligne2,
        ...result,
      } as AttenteCourrierViewItem
    } else if (item.habilitationsVisiteur?.length > 0) {
      const demande = [...item.habilitationsVisiteur]
        .sort((a, b) => {
          if (a.dateDemande < b.dateDemande) return -1
          if (a.dateDemande > b.dateDemande) return 1
          return 0
        })
        .at(0)
      const ligne1 = `Une notification a été envoyée le ${dateFormatter(demande?.dateDemande)} à l'administrateur de votre entreprise`
      return {
        type: 'attente-admin',
        ligne1,
        ...result,
      } as AttenteAdminViewItem
    }
    return undefined
  }

  const habilitations = computed(() => {
    if (!habilitationsEffectives.value) return undefined

    const effectives = habilitationsEffectives.value.map(convertHabilitationToViewItem)
    const enAttente =
      habilitationsDemande.value?.map(convertAttenteHabilitationToViewItem)?.filter(isNotNullOrUndefined) ?? []

    return [...enAttente, ...effectives]
  })

  /* Maj modèle */
  const buildShowSetActivation = (item: HabilitationDTO) => {
    return async (estAccepté: boolean) => {
      if (isReadOnly.value) return

      const title = estAccepté ? 'Confirmer le rattachement' : 'Confirmer la suppression'
      const reponse = await dialog.showDialog({
        title,
        message: '',
        closable: true,
        type: 'YesNo',
      })
      if (reponse !== 'Yes') return

      if (estAccepté) {
        try {
          await store.setHabilitationMembre(item)
          const habilitation = habilitationsEffectives.value!.find((hab) => hab.id === item.id)!
          options.emitAutorisationsChanged('Update', habilitation)
        } catch { }
      } else {
        // Delete
        try {
          await store.deleteHabilitation(item)
          options.emitAutorisationsChanged('Delete', item)
        } catch { }
      }
    }
  }

  const buildShowDeleteHabilitation = (item: HabilitationDTO) => {
    return async () => {
      if (isReadOnly.value) return

      const reponse = await dialog.showDialog({
        message: ['Confirmez-vous la suppression du rattachement ?', '', 'Attention, cette opération est définitive.'],
        type: 'OkCancel',
        closable: true,
        title: 'Suppression rattachement',
      })
      if (reponse === 'Ok') {
        try {
          await store.deleteHabilitation(item)
          options.emitAutorisationsChanged('Delete', item)
        } catch { }
      }
    }
  }

  /* Type de public */
  const getTypesPublicsDropdownItems = (habilitation: HabilitationDTO) => {
    type Result = DropdownItem<TypePublicDropdownItem>
    const resultArray = habilitation.entite.publicsPotentiel.map<Result>((typePublic) => ({
      id: typePublic.type,
      label: TypePublicLibelle[typePublic.type],
    }))
    if (habilitation.entite.publicsPotentiel.length === TypePublic.length) resultArray.push({ id: '*', label: 'Tous' })
    return resultArray
  }

  const getTypesPublicsDropdownValue = (habilitation: HabilitationDTO) => {
    if (habilitation.autorisations.publics.length === TypePublic.length) return '*'
    return habilitation.autorisations.publics.at(0)?.type
  }

  const buildTypePublicDropdownValueUpdate = (habilitation: HabilitationDTO) => {
    return async (type?: TypePublicDropdownItem) => {
      const publics = (() => {
        if (isNullOrUndefined(type)) return null
        if (type === '*') return habilitation.entite.publicsPotentiel
        else return [{ type }]
      })()
      if (!publics) return

      await store.setTypePublic({ habilitation, publics })
      habilitation = habilitationsEffectives.value!.find((h) => h.id === habilitation.id)!
      options.emitAutorisationsChanged('Update', habilitation)
    }
  }

  return {
    habilitations
  }
}
